/**
 * Expected Delivery Interfaces
 *
 * Dieses File definiert die Base-Interfaces, die auch von der LagerApp importiert werden.
 * Base-Interfaces entsprechen den Backend base_hash Methoden.
 */

// ============================================================================
// Konstanten (werden von LagerApp importiert)
// ============================================================================

/**
 * Lieferstatus-Konstanten
 */
export const DELIVERY_STATUS = {
  OPEN: 'open',                         // Normale erwartete Lieferung, Einbuchung möglich
  PARTIAL: 'partial',                   // Teilweise eingebucht
  COMPLETED: 'completed',               // Vollständig eingebucht
  CANCELLED: 'cancelled',               // Storniert
  UNEXPECTED: 'unexpected',             // Unerwartete Lieferung, wartet auf Büro-Freigabe
  PENDING_CONFIRMATION: 'pending_confirmation' // Büro hat freigegeben, Lager muss bestätigen
} as const;

export type DeliveryStatusType = typeof DELIVERY_STATUS[keyof typeof DELIVERY_STATUS];

/**
 * Vordefinierte Handlungsanweisungen
 */
export const HANDLING_INSTRUCTIONS = {
  STOCK_IN: 'STOCK_IN',   // Einlagerung ins Hauptlager
  FORWARD: 'FORWARD'      // Weiterversand an Techniker/Kunden
} as const;

export type HandlingInstructionType = typeof HANDLING_INSTRUCTIONS[keyof typeof HANDLING_INSTRUCTIONS];

// ============================================================================
// Shared Interfaces (werden von LagerApp importiert)
// ============================================================================

/**
 * Gescannter Lieferschein-PDF
 */
export interface DeliveryNotePdf {
  filename: string;
  created_at: string;
  size: number;
}

/**
 * Owner-Typ für Artikel
 */
export type OwnerType = 'BENTOMAX' | 'CUSTOMER';

/**
 * Verpackungstyp
 */
export type PackagingType = 'PALLET' | 'PACKAGE';

// ============================================================================
// Base Interfaces (entsprechen Backend base_hash)
// ============================================================================

/**
 * ExpectedDelivery Base - Gemeinsame Felder aus base_hash
 */
export interface ExpectedDeliveryBase {
  id: number;
  status: DeliveryStatusType;
  supplier_name: string;
  order_nr: string | null;
  project_id: number | null;
  project_name: string | null;
  customer_id: number | null;
  customer_name: string | null;
  packaging_type: PackagingType | null;
  handling_instruction: string;
  expected_from: string | null;
  expected_until: string | null;
  total_ordered: number;
  total_received: number;
  total_open: number;
  created_at: string;
  delivery_note_pdfs: DeliveryNotePdf[];
}

/**
 * ExpectedDeliveryItem Base - Gemeinsame Felder aus base_hash
 */
export interface ExpectedDeliveryItemBase {
  id: number;
  article_template_id: number | null;
  article_template_description: string | null;
  article_template_part_nr: string | null;
  article_template_sn: boolean | null;
  custom_description: string | null;
  display_description: string | null;
  serial_numbers_raw: string[] | null;
  quantity_ordered: number;
  quantity_received: number;
  quantity_open: number;
  owner: OwnerType;
  pending_storage_bin_id: number | null;
  pending_storage_bin_label: string | null;
}

/**
 * ExpectedDeliveryReceipt Base - Gemeinsame Felder aus base_hash
 */
export interface ExpectedDeliveryReceiptBase {
  id: number;
  quantity: number;
  received_at: string;
  received_by_name: string;
  storage_bin_label: string;
  pending_confirmation: boolean;
  article_serial_numbers: string[];
}

// ============================================================================
// Büro-Frontend spezifische Interfaces (erweitern Base)
// ============================================================================

/**
 * ExpectedDelivery für Büro-Frontend (list_hash + detail_hash)
 */
export interface ExpectedDelivery extends ExpectedDeliveryBase {
  // list_hash Felder
  progress_percentage: number;
  items_count: number;
  created_by_name: string;
  has_quick_captured?: boolean;
  // detail_hash Felder (optional)
  external_delivery_note_number?: string | null;
  delivery_note_id?: number | null;
  delivery_note_number?: string | null;
  ticket_id?: number | null;
  ticket_order_nr?: string | null;
  notes?: string | null;
  internal_notification_emails?: string | null;
  external_notification_emails?: string | null;
  order_date?: string | null;
  updated_at?: string;
  created_by_id?: number;
  items?: ExpectedDeliveryItem[];
  quick_captured_deliveries?: { id: number; supplier_name: string | null; pdf_exists: boolean }[];
}

/**
 * ExpectedDeliveryItem für Büro-Frontend (detail_hash)
 */
export interface ExpectedDeliveryItem extends ExpectedDeliveryItemBase {
  article_template_vendor?: string | null;
  fully_received?: boolean;
  created_at?: string;
  _destroy?: boolean; // Für nested attributes
}

/**
 * ExpectedDeliveryReceipt für Büro-Frontend (detail_hash)
 */
export interface ExpectedDeliveryReceipt extends ExpectedDeliveryReceiptBase {
  received_by_id: number;
  storage_bin_id: number;
  warehouse_name: string;
  external_delivery_note_number_scan: string | null;
  article_ids: number[];
  created_at: string;
  article_template_description: string;
}

// ============================================================================
// Parameter Interfaces (Büro-spezifisch)
// ============================================================================

export interface ExpectedDeliveryCreateParams {
  supplier_name: string;
  order_nr?: string;
  external_delivery_note_number?: string;
  delivery_note_id?: number;
  ticket_id?: number;
  project_id?: number;
  customer_id?: number;
  packaging_type?: PackagingType;
  handling_instruction: string;
  notes?: string;
  internal_notification_emails?: string;
  external_notification_emails?: string;
  order_date?: string;
  expected_from?: string;
  expected_until?: string;
  expected_delivery_items_attributes?: Partial<ExpectedDeliveryItem>[];
}

/**
 * Parameter für die Freigabe mit Receipts
 */
export interface ReleaseWithReceiptsParams {
  handling_instruction: string;
  order_nr?: string;
  ticket_id?: number | null;
  project_id?: number | null;
  customer_id?: number | null;
  expected_from?: string | null;
  expected_until?: string | null;
  internal_notification_emails?: string;
  external_notification_emails?: string;
  packaging_type?: PackagingType | null;
  notes?: string;
  delivery_note_id?: number | null;
  items: ReleaseItemParams[];
}

export interface ReleaseItemParams {
  id: number;
  article_template_id: number;
  quantity_ordered?: number;
  owner?: OwnerType;
  storage_bin_id: number;
}
